import { ChevronDown as ExpandMore, ChevronUp as ExpandLess, X as Close } from "lucide-react";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { academyLogger } from "utils/logger";

const clampPrice = (value, min, max) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return min;
    return Math.min(Math.max(numericValue, min), max);
};

const FilterPanel = ({
    handleClearFilters,
    setFilterVisible,
    filteredProducts,
    handleAttributeChange,
    selectedAttributes = {},
    selectedPriceRange = [0, 0],
    setSelectedPriceRange,
    maxPrice = 0,
}) => {
    const [priceCollapsed, setPriceCollapsed] = useState(false);
    const user = useSelector((state) => state?.auth?.data);
    const handlePriceCollapse = () => setPriceCollapsed(!priceCollapsed);
    const currencyCode = filteredProducts?.find((product) => product?.price?.currency)?.price?.currency || "USD";
    const [minSelectedPrice = 0, maxSelectedPrice = maxPrice] = selectedPriceRange;
    const safeMaxPrice = Math.max(0, Math.ceil(Number(maxPrice) || 0));
    // Prices are shown as whole numbers: floor the min and ceil the max so no
    // product is excluded from its own range by rounding.
    const safeMinSelectedPrice = Math.floor(clampPrice(minSelectedPrice, 0, safeMaxPrice));
    const safeMaxSelectedPrice = Math.ceil(clampPrice(maxSelectedPrice, safeMinSelectedPrice, safeMaxPrice));

    academyLogger.info("Filtered Products in FilterPanel:", filteredProducts);

    const optionalAttributes = (filteredProducts || []).flatMap(
        (product) => product.optionalAttributes || []
    );

    // Group the attributes
    const groupedAttributes = optionalAttributes.reduce((acc, { name, values }) => {
        const normalizedName = name.toLowerCase();
        const processedValues = values
            .map((v) => (typeof v === "string" ? v : v?.value))
            .filter(Boolean)
            .map((value) => value.toLowerCase());
        const existingAttribute = acc.find(
            (attr) => attr.name.toLowerCase() === normalizedName
        );

        if (existingAttribute) {
            existingAttribute.values = [
                ...new Set([...existingAttribute.values, ...processedValues]),
            ];
        } else {
            acc.push({ name, values: processedValues });
        }

        return acc;
    }, []);

    academyLogger.info("Grouped Attributes:", groupedAttributes);

    const updatePriceRange = (boundary, value) => {
        if (!setSelectedPriceRange) return;

        setSelectedPriceRange((currentRange = [0, safeMaxPrice]) => {
            const [currentMin = 0, currentMax = safeMaxPrice] = currentRange;
            if (boundary === "min") {
                const nextMin = Math.floor(clampPrice(value, 0, Math.min(currentMax, safeMaxPrice)));
                return [nextMin, currentMax];
            }

            const nextMax = Math.ceil(clampPrice(value, Math.max(0, currentMin), safeMaxPrice));
            return [currentMin, nextMax];
        });
    };

    const handleButtonClick = (attributeName, value) => {
        academyLogger.info("Clicked Attribute:", attributeName, "Value:", value);
        handleAttributeChange?.(attributeName, value);
    };


    return (
        <div className="p-6 h-full flex flex-col relative bg-white shadow-lg rounded-lg transition-all duration-300">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                    <span className="ml-2 font-bold text-gray-800 text-lg">
                        Hello, {user?.firstName || "Please sign in"}
                    </span>
                </div>
                <button
                    onClick={() => setFilterVisible?.(false)}
                    className="text-gray-600 hover:text-[#B88E2F] transition-transform duration-200 hover:scale-110"
                    aria-label="Close filters"
                >
                    <Close />
                </button>
            </div>

            <div className="filter-body flex-grow overflow-y-auto">
                <div className="grid grid-cols-1 gap-6">

                    {/* Price Filter Section */}
                    <div className="w-full">
                        <button
                            onClick={handlePriceCollapse}
                            className={`mb-2 w-full border ${priceCollapsed ? "bg-gray-700" : "bg-[#f4a649]"
                                } text-white rounded-lg p-3 shadow transition-colors duration-300 flex items-center justify-between`}
                        >
                            <span className="font-semibold">Price</span>
                            {priceCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </button>
                        {!priceCollapsed && (
                            <div className="mt-4 rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <span className="font-bold text-gray-800">
                                        Price Range
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500">
                                        {currencyCode}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                                        Min
                                        <input
                                            type="number"
                                            min="0"
                                            max={safeMaxSelectedPrice}
                                            value={safeMinSelectedPrice}
                                            step="1"
                                            onChange={(event) => updatePriceRange("min", event.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-800 outline-none focus:border-[#f4a649] focus:ring-2 focus:ring-[#f4a64933]"
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                                        Max
                                        <input
                                            type="number"
                                            min={safeMinSelectedPrice}
                                            max={safeMaxPrice}
                                            value={safeMaxSelectedPrice}
                                            step="1"
                                            onChange={(event) => updatePriceRange("max", event.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-800 outline-none focus:border-[#f4a649] focus:ring-2 focus:ring-[#f4a64933]"
                                        />
                                    </label>
                                </div>

                                <div className="mt-4 space-y-3">
                                    <input
                                        type="range"
                                        min="0"
                                        max={safeMaxPrice}
                                        value={safeMinSelectedPrice}
                                        step="1"
                                        disabled={safeMaxPrice === 0}
                                        onChange={(event) => updatePriceRange("min", event.target.value)}
                                        className="w-full accent-[#f4a649]"
                                        aria-label="Minimum price"
                                    />
                                    <input
                                        type="range"
                                        min="0"
                                        max={safeMaxPrice}
                                        value={safeMaxSelectedPrice}
                                        step="1"
                                        disabled={safeMaxPrice === 0}
                                        onChange={(event) => updatePriceRange("max", event.target.value)}
                                        className="w-full accent-[#f4a649]"
                                        aria-label="Maximum price"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Optional Attributes Section */}
                    {groupedAttributes.length > 0 && (
                        <div className="w-full">
                            <fieldset className="border border-gray-200 p-4 rounded-lg">
                                <legend className="font-bold text-gray-800 px-2">
                                    More Options
                                </legend>
                                <div className="space-y-4">
                                    {groupedAttributes.map((attribute, index) => (
                                        <div key={index} className="flex flex-col">
                                            <span className="font-semibold text-gray-700 mb-2">
                                                {attribute.name}
                                            </span>
                                            <div className="flex flex-wrap gap-2">
                                                {attribute.values.map((value, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => handleButtonClick(attribute.name, value)}
                                                        className={`border-2 rounded-lg px-3 py-1 text-sm font-semibold transition-colors duration-300
                                                                  ${selectedAttributes[attribute.name]?.includes(value)
                                                                ? "bg-[#f4a649] text-white border-[#f4a649]"
                                                                : "bg-transparent text-gray-700 border-gray-300 hover:bg-[#f4a649] hover:text-white hover:border-[#f4a649]"
                                                            }`}
                                                    >
                                                        {value}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </fieldset>
                        </div>
                    )}

                    {/* Clear Filters Button */}
                    <button
                        onClick={handleClearFilters}
                        className="bg-[#f4a649] text-white rounded-lg py-3 shadow mt-4 w-full hover:bg-gray-700 transition-colors duration-200 font-semibold"
                    >
                        Clear All Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterPanel;
